<template>
  <v-card class="w-100 d-flex flex-column ga-4 pa-8">
    <v-sheet class="header-section">
      <h3 class="title-section">
        Probabilidade de conversões por cidade
        <v-tooltip location="top">
          <template #activator="{ props }">
            <label class="empty-data" v-if="!innerData.success" v-bind="props">
              Dados insuficientes
              <v-icon size="14" class="summary-info" color="grey">
                mdi-help-circle
              </v-icon>
            </label>
          </template>

          Filtro não retornou dados.
        </v-tooltip>
      </h3>
      <p class="subtitle-section">
        Média geral da probabilidade de conversão por cidade atribuída aos registros na última atualização.
      </p>
    </v-sheet>

    <rb-loader v-if="isLoading" v-model="innerData" :contained="true" />

    <FilterComponent
      v-model:view="mode"
      v-model:filters="filtersState"
      v-model:search="search"
      :available-ids="availableIdsByCategory"
      :show-chart="true"
      :show-table="true"
      :show-search="true"
      :show-filter="true"
      :show-refresh="true"
      :enableProcessoSeletivo="false"
      :enableModalidade="true"
      :enableNivelEnsino="true"
      :enableCurso="false"
      :enableOfertaCurso="false"
      :enableEtapa="false"
      :enableFaixaConversao="false"
      :show-download="mode === 'table' && rows && rows.length > 0"
      :download-disabled="!rows || rows.length === 0"
      @click:download="openDownloadCsvPopup"
      @click:chart="mode = 'chart'"
      @click:table="mode = 'table'"
      @click:filter="abrirDrawerFiltros()"
      @submit:search="handleSearchSubmit"
      @click:refresh="reloadData()"
      @update:filters="loadData"
    />

    <v-sheet v-if="mode === 'chart'" class="w-100 d-flex charts-wrap">
      <div ref="brMapRef" style="flex: 1 1 320px; min-width: 320px; height: 460px;"></div>

      <div style="flex: 1 1 50%; height: 460px;">
        <HorizontalBarScroll
          :height="460"
          :rows="barRows"
          :visible-count="7"
          :color-range="['#EDF5F1', '#0EE378', '#000D1C']"
          :wrap-label="(v) => wrapAxisLabel(v, 18, 2)"
          @bar-click="handleBarClick"
        />
      </div>
    </v-sheet>

    <v-sheet v-if="mode === 'table'" class="w-100 d-flex">
      <TableInsights
        :headers="tableHeaders"
        :items="rows"
        :show-total="true"
        total-title="Total geral/Média geral"
        :total-col-span="3"
        :totals="tableTotals"
        v-model:page="page"
        v-model:itemsPerPage="ipp"
        :items-per-page-options="[5,10,25,50]"
        :highlight-index="1"
        @row:click="handleRowClick($event)"
      >
        <template #tooltip-content>
          Exibe a média da <strong>probabilidade de conversão</strong> dos inscritos distribuída por estado, calculada com base no volume de registros e no histórico de captação de cada região.
        </template>
        <template #cell:lt60="{ value }">
          <div class="d-flex align-center justify-end ga-1">
            <span>{{ value }}</span>
            <v-icon
              v-if="pctToNumber(value) >= 15 && pctToNumber(value) < 20"
              size="16"
              color="#FF8A00"
            >mdi-alert-circle</v-icon>
          </div>
        </template>

        <template #total:inscricoes="{ value }">
          {{ Math.round(value).toLocaleString('pt-BR') }}
        </template>

        <template #total:probabilidade="{ value }">
          {{ value.toFixed(2).replace('.', ',') }}%
        </template>
      </TableInsights>
    </v-sheet>

    <DetailPopUp
      v-model="detailPopUpVisible"
      :payload="payloadDetails"
      :url="urlDetailPopUp"
      :title-pop-up="detailPopUpTitle"
      subtitle-pop-up="Encontre o contato para realizar análises qualitativas"
      :headers="[
        { title:'Probabilidade', key:'probabilidade', align:'start' },
        { title:'Nome', key:'nome' },
        { title:'Curso', key:'curso' },
      ]"
      :rows-keys="['id', 'nome', 'curso', 'probabilidade']"
      @after-leave="detailPopUpVisible=false"
    />

    <RbPopUp
      v-model="downloadCsvPopupVisible"
      :width="400"
      confirm-text="Download"
      cancel-text="Cancelar"
      confirm-color="primary-60"
      variant-confirm="elevated"
      variant-cancel="outlined"
      :disabled-confirm="!rows || !rows.length"
      @confirm="downloadTableCsv"
    >
      <template #title>Download do relatório (CSV)</template>

      <template #message>
        Realize o download em CSV do widget<br />
        "<strong>Probabilidade de conversões por cidade</strong>".
      </template>

      <template #confirm-icon>
        <v-icon size="18">mdi-download</v-icon>
      </template>
    </RbPopUp>
  </v-card>
</template>

<script>
import RbLoader from '@/ai_studio/components/common/RbLoader.vue'
import { PostWithJson } from '@/ai_studio/http/http'
import FilterComponent from './FilterComponent.vue'
import TableInsights from './TableInsights.vue'
import DetailPopUp from './detailsPopUp/DetailPopUp.vue'
import HorizontalBarScroll from './charts/HorizontalBar.vue'
import { downloadCsv } from '@/ai_studio/utils/exportCsv'
import RbPopUp from '@/ai_studio/components/common/RBPopup.vue'
import { calculateAvailableIds } from '@/ai_studio/utils/entityHelper'
import * as echarts from 'echarts'

const UF_TO_STATE = {
  AC: 'Acre',
  AL: 'Alagoas',
  AP: 'Amapá',
  AM: 'Amazonas',
  BA: 'Bahia',
  CE: 'Ceará',
  DF: 'Distrito Federal',
  ES: 'Espírito Santo',
  GO: 'Goiás',
  MA: 'Maranhão',
  MT: 'Mato Grosso',
  MS: 'Mato Grosso do Sul',
  MG: 'Minas Gerais',
  PA: 'Pará',
  PB: 'Paraíba',
  PR: 'Paraná',
  PE: 'Pernambuco',
  PI: 'Piauí',
  RJ: 'Rio de Janeiro',
  RN: 'Rio Grande do Norte',
  RS: 'Rio Grande do Sul',
  RO: 'Rondônia',
  RR: 'Roraima',
  SC: 'Santa Catarina',
  SP: 'São Paulo',
  SE: 'Sergipe',
  TO: 'Tocantins'
}

export default {
  name: 'ConversionsByCity',
  components: {
    RbLoader,
    FilterComponent,
    TableInsights,
    DetailPopUp,
    HorizontalBarScroll,
    RbPopUp
  },

  props: {
    data: { type: Object, default: () => ({}) },
    filters: { type: Array, default: () => [] }
  },

  data() {
    return {
      mode: 'chart',
      mapChart: null,
      resizeHandler: null,
      resizeObserver: null,
      resizeRaf: 0,
      mapColorRange: ['#EDF5F1', '#0EE378', '#000D1C'],
      page: 1,
      ipp: 5,
      innerData: this.data ?? {},
      filtersState: this.filters || {},
      originalAvailableIds: null,
      isLoading: false,
      detailPopUpTitle: '',
      urlDetailPopUp: '',
      payloadDetails: {},
      detailPopUpVisible: false,
      dataEmpty: false,
      downloadCsvPopupVisible: false,
      selectedUf: null
    }
  },

  computed: {
    hasData() {
      const conv = Array.isArray(this.innerData?.dados_convertion)
        ? this.innerData.dados_convertion
        : []
      return conv.length > 0
    },

    rows() {
      const items = Array.isArray(this.innerData?.dados_city)
        ? this.innerData.dados_city
        : Array.isArray(this.innerData)
          ? this.innerData
          : []

      return items
        .map(item => {
          const inscricoes = Number(item.inscricoes) || 0
          const probabilidade = Number(item.media) || 0

          return {
            city: item.cidade || 'Sem cidade definida',
            uf: item.uf,
            idCidade: item.idCidade,
            estado: item.estado || 'Sem estado definido',
            inscricoes,
            probabilidade: `${probabilidade}%`,
            modalidade_id: item.modalidade,
            nivel_ensino_id: item.nivelEnsino,
            oferta: item.oferta
          }
        })
        .sort((a, b) => b.inscricoes - a.inscricoes)
    },

    barRows() {
      const cityAgg = this.buildBarSeriesFromCity()
      if (!Array.isArray(cityAgg) || !cityAgg.length) return []

      const sortedByInscrDesc = [...cityAgg].sort((a, b) => b.inscricoes - a.inscricoes)
      const topN = sortedByInscrDesc.slice(0, 200)

      return topN.map(d => ({
        idCidade: d.id,
        cidade: d.name,
        inscricoes: d.inscricoes,
        probabilidade: d.probabilidade
      }))
    },

    tableTotals() {
      const items = this.rows
      if (!Array.isArray(items) || items.length === 0) {
        return { inscricoes: 0, probabilidade: '0%' }
      }

      const parseNumberBR = (value) => {
        if (value === null || value === undefined) return 0
        let s = String(value).trim()
        s = s.replace(/[^\d,.-]/g, '')

        if (s.includes(',') && s.includes('.')) {
          s = s.replace(/\./g, '').replace(',', '.')
        } else if (s.includes(',')) {
          s = s.replace(',', '.')
        }

        const n = parseFloat(s)
        return Number.isFinite(n) ? n : 0
      }

      const parseInscricoes = (value) => {
        if (value === null || value === undefined) return 0
        let s = String(value).trim()
        s = s.replace(/[^\d]/g, '')
        const n = parseInt(s, 10)
        return Number.isFinite(n) ? n : 0
      }

      let totalInscricoes = 0
      let weightedProbSum = 0
      let totalWeight = 0

      for (const item of items) {
        const inscricoes = parseInscricoes(item.inscricoes)

        let probabilidade = parseNumberBR(item.probabilidade)
        if (probabilidade > 0 && probabilidade <= 1) probabilidade *= 100

        totalInscricoes += inscricoes

        const weight = inscricoes
        if (weight > 0) {
          weightedProbSum += probabilidade * weight
          totalWeight += weight
        }
      }

      const avgProbabilidade = totalWeight > 0 ? (weightedProbSum / totalWeight) : 0

      return {
        inscricoes: totalInscricoes,
        probabilidade: `${avgProbabilidade.toFixed(2)}%`
      }
    },

    availableIdsByCategory() {
      if (this.originalAvailableIds) {
        return this.originalAvailableIds
      }
      return calculateAvailableIds(this.innerData)
    },

    tableHeaders() {
      return [
        { title:'Cidade', key:'city' },
        { title:'Estado', key:'estado' },
        { title:'', key:'' },
        { title:'', key:'' },
        { title:'Inscrições', key:'inscricoes', align:'start' },
        { title:'Probabilidade', key:'probabilidade', align:'start' },
      ]
    },
  },

  watch: {
    data: {
      deep: true,
      immediate: true,
      handler(v) {
        this.innerData = v ?? {}
        if (v && Object.keys(v).length > 0 && !this.originalAvailableIds) {
          const ids = calculateAvailableIds(v)
          if (Object.keys(ids).length > 0) {
            this.originalAvailableIds = ids
          }
        }
      }
    },

    filters: {
      deep: true,
      immediate: true,
      handler(v) {
        this.filtersState = v || {}
      }
    },

    mode: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal === 'chart') {
          this.$nextTick(() => this.initCharts())
        } else if (oldVal === 'chart') {
          this.disposeCharts()
        }
      }
    }
  },

  beforeUnmount() {
    this.disposeCharts()
  },

  methods: {
    openDownloadCsvPopup() {
      if (!this.rows || !this.rows.length) return
      this.downloadCsvPopupVisible = true
    },

    downloadTableCsv() {
      const fields = ['Cidade', 'Estado', 'Inscrições', 'Probabilidade']

      const data = (this.rows || []).map(row => {
        const uf = (row.uf || '').toUpperCase()
        const estado = uf ? (UF_TO_STATE[uf] || uf) : ''

        const inscr = Number(row.inscricoes) || 0
        const prob = this.pctToNumber(row.probabilidade)

        return [
          row.city || '',
          estado,
          inscr.toLocaleString('pt-BR'),
          `${prob.toFixed(2).replace('.', ',')}%`
        ]
      })

      if (this.tableTotals) {
        const totalInscricoes = Number(this.tableTotals.inscricoes) || 0
        const totalProbabilidades = this.pctToNumber(this.tableTotals.probabilidade)

        data.push([
          'Total geral/Média geral',
          '',
          totalInscricoes.toLocaleString('pt-BR'),
          `${totalProbabilidades.toFixed(2).replace('.', ',')}%`
        ])
      }

      const today = new Date().toISOString().slice(0, 10)
      downloadCsv({
        filename: `probabilidade_conversoes_por_cidade_${today}.csv`,
        fields,
        data,
        delimiter: ';'
      })
    },

    disposeCharts() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
      if (this.resizeRaf) {
        cancelAnimationFrame(this.resizeRaf)
        this.resizeRaf = 0
      }
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }
      if (this.mapChart) {
        this.mapChart.dispose()
        this.mapChart = null
      }
    },

    setupMapResizeObserver() {
      const el = this.$refs.brMapRef
      if (!el || this.resizeObserver) return

      this.resizeObserver = new ResizeObserver(() => {
        if (!this.mapChart) return

        if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf)
        this.resizeRaf = requestAnimationFrame(() => {
          this.mapChart.resize()
          this.applyResponsiveMapLayout()
        })
      })

      this.resizeObserver.observe(el)
    },

    applyResponsiveMapLayout() {
      if (!this.mapChart) return

      const w = this.mapChart.getWidth()
      const isNarrow = w <= 420

      this.mapChart.setOption(
        {
          title: {
            bottom: isNarrow ? 70 : 85,
            textStyle: {
              fontSize: isNarrow ? 12 : 14,
              color: '#191C1C',
              fontWeight: 'bold',
              fontFamily: 'Roboto, sans-serif'
            }
          },
          visualMap: {
            seriesIndex: 0,
            orient: 'horizontal',
            left: 'center',
            bottom: isNarrow ? 18 : 50,
            textStyle: { color: '#191C1C', fontSize: isNarrow ? 12 : 14 },
            itemWidth: isNarrow ? 12 : 15,
            itemHeight: isNarrow ? 140 : 180,
            inRange: { color: this.mapColorRange }
          },
          series: [
            {
              layoutCenter: ['50%', isNarrow ? '40%' : '38%'],
              layoutSize: isNarrow ? '88%' : '75%',
              aspectScale: 0.9
            }
          ]
        },
        { notMerge: false, lazyUpdate: true }
      )
    },

    wrapAxisLabel(value, maxCharsPerLine = 18, maxLines = 2) {
      const text = String(value ?? '')
      if (!text) return ''

      const words = text.split(' ')
      const lines = []
      let line = ''

      for (const w of words) {
        const next = line ? `${line} ${w}` : w

        if (next.length <= maxCharsPerLine) {
          line = next
        } else {
          if (line) lines.push(line)
          line = w

          if (line.length > maxCharsPerLine) {
            lines.push(line.slice(0, maxCharsPerLine))
            line = line.slice(maxCharsPerLine)
          }
        }

        if (lines.length === maxLines) break
      }

      if (lines.length < maxLines && line) lines.push(line)
      return lines.slice(0, maxLines).join('\n')
    },

    abrirDrawerFiltros() {},

    handleRowClick(row) {
      this.detailPopUpTitle = `${row.city}`
      this.detailPopUpVisible = true
      this.urlDetailPopUp = 'insights/getReportForecastByCity'

      const modalidade = this.filtersState.modalidade && this.filtersState.modalidade.length > 0
        ? (this.filtersState.modalidade.length === 1 ? this.filtersState.modalidade[0] : this.filtersState.modalidade)
        : ''

      const nivelEnsino = this.filtersState.nivelEnsino && this.filtersState.nivelEnsino.length > 0
        ? (this.filtersState.nivelEnsino.length === 1 ? this.filtersState.nivelEnsino[0] : this.filtersState.nivelEnsino)
        : ''

      const processo = this.filtersState.processo && this.filtersState.processo.length > 0
        ? (this.filtersState.processo.length === 1 ? this.filtersState.processo[0] : this.filtersState.processo)
        : ''

      this.payloadDetails = {
        city: row.idCidade,
        modality: modalidade,
        education_level: nivelEnsino,
        process: processo
      }
    },

    handleBarClick(d) {
      this.detailPopUpTitle = `${d.cidade}`
      this.detailPopUpVisible = true
      this.urlDetailPopUp = 'insights/getReportForecastByCity'
      this.payloadDetails = { city: d.idCidade }
    },

    applyIncomingData(payload) {
      this.selectedUf = null
      if (!payload || typeof payload !== 'object') {
        this.innerData = {}
        this.disposeCharts()
        return
      }

      this.innerData = payload

      const dados = Array.isArray(payload.dados_city) ? payload.dados_city : []
      if (!dados.length) {
        this.disposeCharts()
        this.$nextTick(() => this.initCharts())
        return
      }

      if (this.mode === 'chart') {
        this.$nextTick(() => {
          this.disposeCharts()
          this.initCharts()
        })
      }
    },

    pctToNumber(value) {
      if (typeof value === 'number') return value
      if (typeof value !== 'string') return 0
      return Number(value.replace('%', '').replace(',', '.')) || 0
    },

    buildSeriesFromData() {
      const items = Array.isArray(this.innerData?.dados_city)
        ? this.innerData.dados_city
        : Array.isArray(this.innerData)
          ? this.innerData
          : []

      const byUf = new Map()
      for (const row of items) {
        const uf = (row?.uf || '').toUpperCase()
        const media = Number(row?.media)
        const peso = Number(row?.inscricoes)

        if (!uf || !Number.isFinite(media) || !Number.isFinite(peso) || peso <= 0) continue

        if (!byUf.has(uf)) byUf.set(uf, { wsum: 0, w: 0 })
        const acc = byUf.get(uf)
        acc.wsum += media * peso
        acc.w += peso
      }

      return Object.entries(UF_TO_STATE).map(([uf, stateName]) => {
        const acc = byUf.get(uf)
        const avg = acc && acc.w > 0 ? (acc.wsum / acc.w) : 0
        const value = Math.max(0, Math.min(100, Number(avg.toFixed(2))))
        const inscricoes = acc?.w ?? 0
        return { name: stateName, value, inscricoes, uf }
      })
    },

    buildBarSeriesFromCity() {
      const items = Array.isArray(this.innerData?.dados_city)
        ? this.innerData.dados_city
        : Array.isArray(this.innerData)
          ? this.innerData
          : []

      const byCity = new Map()

      for (const row of items) {
        if (this.selectedUf && (row?.uf || '').toUpperCase() !== this.selectedUf) continue

        const cityId = row?.idCidade
        if (cityId == null) continue

        const prob = Number(row?.media)
        const inscr = Number(row?.inscricoes)

        if (!Number.isFinite(prob) || !Number.isFinite(inscr) || inscr <= 0) continue

        if (!byCity.has(cityId)) {
          byCity.set(cityId, {
            id: cityId,
            name: row?.cidade || 'Sem cidade definida',
            wsum: 0,
            w: 0,
            inscricoes: 0
          })
        }

        const acc = byCity.get(cityId)
        acc.wsum += prob * inscr
        acc.w += inscr
        acc.inscricoes += inscr
      }

      return Array.from(byCity.values()).map(acc => {
        const avg = acc.w > 0 ? acc.wsum / acc.w : 0
        const probabilidade = Math.max(0, Math.min(100, avg))

        return {
          id: acc.id,
          name: acc.name,
          inscricoes: acc.inscricoes,
          probabilidade
        }
      })
    },

    async initCharts() {
      if (!this.$refs.brMapRef) return

      if (this.mapChart) {
        this.mapChart.resize()
        this.applyResponsiveMapLayout()
        return
      }

      const mapSeriesData = this.buildSeriesFromData()

      await this.initBrazilMap(mapSeriesData, this.mapColorRange)

      this.setupMapResizeObserver()

      this.$nextTick(() => {
        if (this.mapChart) {
          this.mapChart.resize()
          this.applyResponsiveMapLayout()
        }
      })
    },

    async initBrazilMap(seriesData, colorRange) {
      if (!this.$refs.brMapRef) return

      const res = await fetch('/maps/brazil-states.geojson', { cache: 'force-cache' })
      const geojson = await res.json()
      echarts.registerMap('BRA', geojson)

      const el = this.$refs.brMapRef
      this.mapChart = echarts.init(el)

      const mapOption = {
        title: {
          text: 'Probabilidade de conversão:',
          left: 'center',
          bottom: 85,
          textStyle: {
            fontSize: 14,
            color: '#191C1C',
            fontWeight: 'bold',
            fontFamily: 'Roboto, sans-serif'
          }
        },
        tooltip: {
          trigger: 'item',
          axisPointer: { type: 'shadow' },
          confine: true,
          backgroundColor: '#fff',
          borderColor: '#C5C7C6',
          borderWidth: 1,
          padding: [8, 10],
          textStyle: { color: '#191C1C', fontSize: 12 },
          formatter: (params) => {
            const arr = Array.isArray(params) ? params : [params]
            if (!arr.length) return ''

            const hasInscricoes = arr.some(p => p?.data?.inscricoes != null)
            const sorted = hasInscricoes
              ? arr.slice().sort(
                  (a, b) =>
                    Number(b.data?.inscricoes ?? 0) -
                    Number(a.data?.inscricoes ?? 0)
                )
              : arr

            let tip = ''
            sorted.forEach(p => {
              const name = p.name || p.data?.name || ''
              const value = Number(
                p.value ?? p.data?.value ?? p.data?.media ?? p.data?.probabilidade ?? 0
              )
              const inscr = Number(p.data?.inscricoes ?? 0)

              tip += `<strong>${name}</strong><br/>`
              tip += `${p.marker} <strong>Conversões:</strong> ${value.toFixed(2)}%`

              if (p.data?.inscricoes != null) {
                tip += `<br/>&nbsp;&nbsp;Inscrições: ${inscr.toLocaleString('pt-BR')}`
              }

              tip += `<br/>`
            })

            return tip
          }
        },
        visualMap: {
          type: 'continuous',
          min: 0,
          max: 100,
          seriesIndex: 0,
          orient: 'horizontal',
          left: 'center',
          bottom: 20,
          text: ['100%', '0%'],
          textStyle: { color: '#191C1C', fontSize: 14 },
          textGap: 10,
          itemWidth: 15,
          itemHeight: 180,
          calculable: false,
          inRange: { color: colorRange },
          formatter: (value) => value + '%'
        },
        series: [
          {
            name: 'Conversões',
            type: 'map',
            map: 'BRA',
            roam: false,
            emphasis: { label: { show: false } },
            itemStyle: { borderColor: '#000', borderWidth: 0.6 },
            layoutCenter: ['50%', '38%'],
            layoutSize: '75%',
            aspectScale: 0.9,
            data: seriesData
          }
        ]
      }

      this.mapChart.setOption(mapOption, { notMerge: true })
      this.applyResponsiveMapLayout()

      this.mapChart.on('click', (params) => {
        if (params.data && params.data.uf) {
          const clickedUf = params.data.uf.toUpperCase()
          this.selectedUf = this.selectedUf === clickedUf ? null : clickedUf
        }
      })
    },

    async reloadData() {
      this.originalAvailableIds = null
      await this.loadData(this.filtersState)
    },

    handleSearchSubmit(searchValue) {
      this.loadData({ search: searchValue })
    },

    async loadData(newFilters) {
      this.isLoading = true

      if (newFilters) this.filtersState = newFilters

      const body = {
        process: this.filtersState.processo,
        education_level: this.filtersState.nivelEnsino,
        modality: this.filtersState.modalidade,
        course: this.filtersState.curso,
        step: this.filtersState.etapa,
        conversion_range: this.filtersState.faixaConversao,
        search: this.filtersState.search
      }

      const url = 'insights/getReportCity'

      try {
        const responseData = await PostWithJson(url, JSON.stringify(body))
        this.innerData = responseData
        this.applyIncomingData(responseData)
      } catch (error) {
        console.error('Erro ao carregar dados de cidade:', error)
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style>
.charts-wrap {
  gap: 16px;
  flex-wrap: wrap;
  align-items: stretch;
}

.header-section {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: start;
  gap: 8px;
  align-self: stretch;
}
.title-section {
  color: var(--Text-Neutral-Primary, #191C1C);
  font-family: Roboto, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
}
.subtitle-section {
  color: var(--Text-Neutral-Primary, #191C1C);
  font-family: Roboto, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
}

.empty-data {
  color: var(--Text-Neutral-Subtle, #505353);
  font-family: var(--Typescale-Body-Medium-Font-family, Roboto);
  font-size: var(--Typescale-Body-Medium-Font-size, 14px);
  font-style: italic;
  font-weight: 400;
  line-height: var(--Typescale-Body-Medium-Line-height, 20px);
  letter-spacing: var(--Typescale-Body-Medium-Tracking, 0.25px);
}
</style>
